import { useState, useCallback, useRef } from 'react';
import { ref, uploadBytesResumable, getDownloadURL } from 'firebase/storage';
import { storage } from '@/lib/firebase';

const MAX_FILE_SIZE = 0; // 0 = no limit

export interface UploadProgress {
  percent: number;
  bytesTransferred: number;
  totalBytes: number;
  bytesPerSecond: number;
}

interface UseVideoUploadReturn {
  upload: (file: File, userId: string) => Promise<string>;
  progress: UploadProgress;
  uploading: boolean;
  error: string | null;
  reset: () => void;
}

const INITIAL_PROGRESS: UploadProgress = {
  percent: 0,
  bytesTransferred: 0,
  totalBytes: 0,
  bytesPerSecond: 0,
};

export function useVideoUpload(): UseVideoUploadReturn {
  const [progress, setProgress] = useState<UploadProgress>(INITIAL_PROGRESS);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const startTimeRef = useRef<number>(0);
  const lastBytesRef = useRef<number>(0);
  const lastTimeRef = useRef<number>(0);

  const reset = useCallback(() => {
    setProgress(INITIAL_PROGRESS);
    setUploading(false);
    setError(null);
  }, []);

  const upload = useCallback(async (file: File, userId: string): Promise<string> => {
    if (!file.type.startsWith('video/')) {
      throw new Error('Only video files are allowed');
    }

    if (MAX_FILE_SIZE > 0 && file.size > MAX_FILE_SIZE) {
      throw new Error(`File size exceeds limit`);
    }

    setError(null);
    setUploading(true);
    startTimeRef.current = Date.now();
    lastBytesRef.current = 0;
    lastTimeRef.current = Date.now();
    setProgress({
      ...INITIAL_PROGRESS,
      totalBytes: file.size,
    });

    const timestamp = Date.now();
    const safeName = file.name.replace(/[^a-zA-Z0-9._-]/g, '_');
    const filePath = `aicl-object-replacement/videos/${userId}/${timestamp}_${safeName}`;
    const storageRef = ref(storage, filePath);

    return new Promise<string>((resolve, reject) => {
      const uploadTask = uploadBytesResumable(storageRef, file, {
        contentType: file.type,
      });

      uploadTask.on(
        'state_changed',
        (snapshot) => {
          const now = Date.now();
          const elapsed = (now - lastTimeRef.current) / 1000;
          const bytesDelta = snapshot.bytesTransferred - lastBytesRef.current;

          let speed = 0;
          if (elapsed > 0.5) {
            speed = bytesDelta / elapsed;
            lastBytesRef.current = snapshot.bytesTransferred;
            lastTimeRef.current = now;
          }

          setProgress((prev) => ({
            percent: Math.round((snapshot.bytesTransferred / snapshot.totalBytes) * 100),
            bytesTransferred: snapshot.bytesTransferred,
            totalBytes: snapshot.totalBytes,
            bytesPerSecond: speed > 0 ? speed : prev.bytesPerSecond,
          }));
        },
        (err) => {
          setError(err.message);
          setUploading(false);
          reject(err);
        },
        async () => {
          setUploading(false);
          setProgress((prev) => ({ ...prev, percent: 100 }));
          await getDownloadURL(uploadTask.snapshot.ref);
          resolve(filePath);
        },
      );
    });
  }, []);

  return { upload, progress, uploading, error, reset };
}
